/**
 * Modern confirm dialog utility using React Portal
 *
 * Provides an async/await interface for confirmation dialogs while maintaining
 * visual parity with the legacy context.confirm implementation.
 *
 * Usage:
 *   if (await confirm('Delete this item?')) { ... }
 *
 *   const result = await confirm(
 *     <p><strong>Warning:</strong> Changes will be visible to patients.</p>,
 *     {
 *       acceptText: 'Yes, Save',
 *       rejectText: 'No, Cancel',
 *       titleText: 'Confirm Changes'
 *     }
 *   );
 */

import ReactDOM from 'react-dom';
import ConfirmDialog from '@components/Widgets/ConfirmDialog.react';

// Global container for confirm dialogs
let confirmContainer = null;

/**
 * Show a confirmation dialog and return a Promise that resolves to boolean
 *
 * @param {string|React.Element} contents - Message string or JSX element
 * @param {Object} options - Configuration options
 * @param {string} options.acceptText - Text for accept button (default: 'Ok')
 * @param {string} options.rejectText - Text for reject button (default: 'Cancel')
 * @param {string} options.titleText - Optional dialog title
 * @param {string} options.position - Dialog position: 'centered', 'top', etc (default: 'centered')
 * @param {string} options.rejectDefaultClass - Custom CSS class for reject button
 * @returns {Promise<boolean>} Promise that resolves to true if accepted, false if rejected
 */
export const confirm = (contents, options = {}) => {
    return new Promise((resolve) => {
        // A fresh container per dialog, appended last so the dialog always
        // stacks above any modal that was already open when confirm() ran
        if (!confirmContainer) {
            confirmContainer = document.createElement('div');
            confirmContainer.id = 'confirm-dialog-portal';
        }
        document.body.appendChild(confirmContainer);

        // Configure options with defaults matching the old implementation
        const opts = {
            acceptText: 'Ok',
            rejectText: 'Cancel',
            titleText: '',
            position: 'centered',
            rejectDefaultClass: null,
            ...options,
        };

        // Handle resolution and cleanup
        const handleResolve = (result) => {
            resolve(result);

            // Unmount the dialog using legacy API and detach the container so
            // the next confirm() re-appends it above whatever is open then
            ReactDOM.unmountComponentAtNode(confirmContainer);

            if (confirmContainer.parentNode) {
                confirmContainer.parentNode.removeChild(confirmContainer);
            }
        };

        // Render the dialog using legacy API
        ReactDOM.render(
            <ConfirmDialog
                contents={contents}
                acceptText={opts.acceptText}
                rejectText={opts.rejectText}
                titleText={opts.titleText}
                position={opts.position}
                rejectDefaultClass={opts.rejectDefaultClass}
                onResolve={handleResolve}
            />,
            confirmContainer
        );
    });
};

/**
 * Clean up the confirm dialog system (useful for testing or app teardown)
 */
export const cleanupConfirm = () => {
    if (confirmContainer) {
        ReactDOM.unmountComponentAtNode(confirmContainer);

        if (confirmContainer.parentNode) {
            confirmContainer.parentNode.removeChild(confirmContainer);
        }

        confirmContainer = null;
    }
};

export default confirm;
